<template>
  <span class="d-contents">
  <v-btn icon @click='handleSpeech' v-if="speechEnabled">
    <span v-html="svg"></span>
  </v-btn>
  <v-snackbar
      v-model="snackbar"
      timeout="-1"
      :transition="false"
      content-class="snackbar-wrapper"
      elevation="24"
    >
    {{ message }}

    <template v-slot:action="{ attrs }">

      <v-btn
        color="pink"
        text
        v-bind="attrs"
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </template>
  </v-snackbar>
  </span>
</template>

<script>
  import { defineComponent, ref, getCurrentInstance, computed, watch } from 'vue';
  import { useEvent } from '../../plugins/vue-gtag';

  const maxRecordingDuration = 7000;
  const timeBetweenSubmisions = 700;

export default defineComponent({
  events: ['speech'],
  props: {
    language: {
      type: String
    },
    locale: {
      type: String
    }
  },
  setup(props, context){
    const instance = getCurrentInstance();
    const iFrame = (window.location !== window.parent.location);
    const speechEnabled = !!(window?.MediaRecorder || window?.MediaDevices) && !iFrame;
    const speechPermissionStatus = ref(false);
    const snackbar = ref(false);
    const message = ref('');
    const recording = ref(false);
    const submisionInProgress = ref(false);
    const recorder = ref(null);
    const stopped = ref(false);
    const gaSpeechAttempt = useEvent('speech_attempt');
    const gaSpeechRecognized = useEvent('speech_recognized');
    const svg = computed(()=>{
      if(!recording.value){
        return '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" data-icon="mdi:microphone" class="iconify" style="vertical-align: -0.125em; transform: rotate(360deg);"><path fill="currentColor" d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3m7 9c0 3.53-2.61 6.44-6 6.93V21h-2v-3.07c-3.39-.49-6-3.4-6-6.93h2a5 5 0 0 0 5 5a5 5 0 0 0 5-5h2Z"></path></svg>'
      } else {
        return '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" data-icon="mdi:stop" class="iconify pulse" style="vertical-align: -0.125em; transform: rotate(360deg);"><path fill="currentColor" d="M18 18H6V6h12v12Z"></path></svg>';
      }
    })

    navigator.permissions.query({name: 'microphone'}).then(function (result) {
      speechPermissionStatus.value = result.state
      console.log(result);
      result.onchange = function () {};
    });

    const stop = () => {
      if(recorder.value){
        recorder.value.stop();
      }
    }

    watch(()=> props.locale, () => {
      if(snackbar.value){
        _handleSpeech();
      }
    });

    const handleSpeech = () => {
      if(!recorder?.value){
        gaSpeechAttempt();
      }
      _handleSpeech();
    }

    const _handleSpeech = async () => {
      snackbar.value = false; // forces snabar animation in subsequent clicks
      if(speechPermissionStatus.value === 'denied'){
        setTimeout(() => {
          snackbar.value = true;
          message.value = instance.proxy.$i18n.t('speech.no-permission');
        }, 10);
        return;
      }
      if(recording.value){
        recorder.value.stop();
        return;
      }
      stopped.value = false;
      // console.log('click microphone btn');
      // console.log(speechEnabled);

      let userDevices;
      await navigator.mediaDevices.enumerateDevices().then((devices) => {
        console.log(devices)
        userDevices = devices.filter((d) => d.kind === 'audioinput');
      });
      if(userDevices.length === 0){
        message.value = instance.proxy.$i18n.t('speech.no-mic');
        snackbar.value = true;
      } else {
        await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: userDevices[0].deviceId,
          },
        }).then(handleRecord);
      }
    }

    const handleRecord = function(stream) {
      
      recording.value = true;
      const options = {mimeType: 'audio/webm'};
      const recordedChunks = [];
      recorder.value = new MediaRecorder(stream, options);
      // set maximum duration of recording
      setTimeout(() => { recorder?.value?.stop() }, maxRecordingDuration);

      recorder.value.addEventListener('dataavailable', async function(e) {
        // console.log('dataavailable');
        recordedChunks.push(e.data);
        if(e.data.size > 0){
          if(!submisionInProgress.value && !stopped.value){
            submisionInProgress.value = true;
            try {
              let data = new Blob(recordedChunks, {type: 'audio/mpeg'});
              let formData = new FormData();
              formData.append('file', data);
              let result = await fetch(`${process.env.VUE_APP_WHISPER_URL}/whisper/${props.language}`, {
                body: formData,
                method: "post"
              });
              let response = await result.json();
              // console.log(response);
              // console.log(response.results[0].transcript);
              (() => { // in order to deal with async behavior
                instance.proxy.$parent.$emit('speech', response.results[0].transcript.trim());
              })();
            } catch (error) {
              console.log(error);
            } finally {
              submisionInProgress.value = false;
            }
          }
        }
      });

      recorder.value.addEventListener('start', async function() {
        instance.proxy.$parent.$emit('startspeech');
      });
      recorder.value.addEventListener('stop', async function() {
        // console.log('stop recording');
        recorder.value.stream.getTracks().forEach(track => track.stop());
        recording.value = false;
        recorder.value = null;
        stopped.value = true;
        instance.proxy.$parent.$emit('endspeech');
        gaSpeechRecognized();
      });

      recorder.value.start(timeBetweenSubmisions);
    };

    return {
      speechEnabled,
      handleSpeech,
      snackbar,
      message,
      stop,
      recording,
      svg
    }
  },
})
</script>

<style lang="scss">
  .v-snack__wrapper {
    -webkit-animation: bounce 1s ease-out;
    -moz-animation: bounce 700ms ease-out;
    -o-animation: bounce 700ms ease-out;
    animation: bounce 1s ease-out;
  }

  .theme--dark .v-snack__wrapper {
    background-color: var(--light-main-bg-color) !important;
    div, span {
      color: var(--light-text) !important;
    }
  }

  .theme--light .v-snack__wrapper {
    background-color: var(--light-accent-bg) !important;
    div, span {
      color: var(--dark-text) !important;
    }
  }

  @-webkit-keyframes bounce {
    0% { -webkit-transform:translateY(0%); }
    8% { -webkit-transform:translateY(-40%); }
    20% { -webkit-transform:translateY(0%); }
    35% { -webkit-transform:translateY(-20%);}
    45% { -webkit-transform:translateY(0%); }
  }

  /* Mozilla Firefox 15 below */
  @-moz-keyframes bounce {
    0% { -moz-transform:translateY(0%); }
    8% { -moz-transform:translateY(-40%); }
    20% { -moz-transform:translateY(0%); }
    35% { -moz-transform:translateY(-20%);}
    45% { -moz-transform:translateY(0%); }
  }

  /* Opera 12.0 */
  @-o-keyframes bounce {
    0% { -o-transform:translateY(0%); }
    8% { -o-transform:translateY(-40%); }
    20% { -o-transform:translateY(0%); }
    35% { -o-transform:translateY(-20%);}
    45% { -o-transform:translateY(0%); }
  }

  /* W3, Opera 12+, Firefox 16+ */
  @keyframes bounce {
    0% { transform:translateY(0%); }
    8% { transform:translateY(-40%); }
    20% { transform:translateY(0%); }
    35% { transform:translateY(-20%);}
    45% { transform:translateY(0%); }
  }

  .pulse {
    animation: pulse 1s infinite ease-in-out alternate;
  }
  @keyframes pulse {
    from { transform: scale(0.8); }
    to { transform: scale(1.2); }
  }
</style>

